import Big from 'big.js';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import {
  RegisterBanksSelect,
  mapCashMovementDirectionToBankType,
} from '@/modules/common/components/RegisterBanksSelect.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { withdrawAllAmount } from '@/modules/issuer-dashboard/components/portfolios/withdrawAllAmount.ts';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import {
  useIssuerAddPortfolioCashMovementMutation,
  useIssuerCurrencyHistoricalFxRateQuery,
  useIssuerUpdatePortfolioCashMovementMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
  SelectWithCreatableOption,
  Spinner,
  TextInputField,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { InfoCircleIcon } from 'ui/icons';
import { getDisableTime, goodFormatWithCurrency } from 'ui/utils';
import {
  CashMovementType,
  CreatePortfolioCashMovementRequest,
  CurrencyEnum,
  PaymentStatus,
  PortfolioCashMovementPaginateResponse,
  RegisterBankExtended,
  UpdatePortfolioCashMovementRequest,
} from 'api';

interface IssuerAddCashMovementModalProps extends BaseModalProps {
  item?: PortfolioCashMovementPaginateResponse;
  portfolioId: number;
}

interface CreatePortfolioCashMovementWithTotalRequest extends CreatePortfolioCashMovementRequest {
  total?: number;
}

const validationSchema = yup.object<CreatePortfolioCashMovementWithTotalRequest>({
  shareholder_id: yup.number().when(['direction'], ([direction], schema) => {
    return direction === CashMovementType.InvestorToFund ||
      direction === CashMovementType.FundToInvestor
      ? schema
          .typeError('Shareholder id must be a number')
          .required('This field is required')
          .moreThan(0, 'This field is required')
      : schema.nullable();
  }),
  status: yup.mixed<PaymentStatus>().oneOf(Object.values(PaymentStatus)),
  direction: yup
    .mixed<CashMovementType>()
    .oneOf(Object.values(CashMovementType))
    .required('This field is required'),
  amount: yup
    .number()
    .typeError('Amount must be a number')
    .moreThan(0, 'Amount must be more than 0')
    .required('This field is required'),
  memo: yup.string().trim(),
  requested_at_date: yup
    .string()
    .trim()
    .required('This field is required')
    .test(
      'not-in-future',
      'Date cannot be in the future',
      value => !value || !dayjs(value).isAfter(dayjs()),
    ),
  confirmation_date: yup
    .string()
    .trim()
    .when(['status', 'requested_at_date'], ([status], schema) => {
      return status === PaymentStatus.Completed
        ? schema
            .required('This field is required')
            .test('', "Confirmation date can't be before requested date", function (value) {
              const { requested_at_date } = this.parent;
              return requested_at_date && value
                ? dayjs(value).isAfter(dayjs(requested_at_date)) ||
                    dayjs(value).isSame(dayjs(requested_at_date), 'day')
                : true;
            })
        : schema.nullable();
    })
    .test(
      'not-in-future',
      'Date cannot be in the future',
      value => !value || !dayjs(value).isAfter(dayjs()),
    ),
  from_bank_id: yup.number().when(['direction'], ([direction], schema) => {
    return direction === CashMovementType.FundToInvestor ||
      direction === CashMovementType.BankToBank
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  to_bank_id: yup.number().when(['direction'], ([direction], schema) => {
    return direction === CashMovementType.InvestorToFund ||
      direction === CashMovementType.BankToBank
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  fx_rate: yup.number().when(['direction'], ([direction], schema) => {
    return direction === CashMovementType.BankToBank
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
});

const initialValues: CreatePortfolioCashMovementWithTotalRequest = {
  shareholder_id: undefined,
  status: undefined,
  direction: undefined,
  amount: 0,
  memo: '',
  total: undefined,
  requested_at_date: undefined,
  confirmation_date: undefined,
  from_bank_id: undefined,
  to_bank_id: undefined,
  transaction_id: undefined,
  dividend_event_id: undefined,
  is_preview_distribution: undefined,
  fx_rate: undefined,
  portfolio_id: undefined,
  payment_id: undefined,
};

export const mapCashMovementTypeToText: Partial<Record<CashMovementType, string>> = {
  [CashMovementType.CashInToPortfolio]: 'Deposit',
  [CashMovementType.CashOutOfPortfolio]: 'Withdrawal',
  [CashMovementType.BankToBank]: 'Internal movement',
  [CashMovementType.InvestorToFund]: 'Deposit',
  [CashMovementType.FundToInvestor]: 'Withdrawal',
};

export const mapCashMovementTypeToDescription: Partial<Record<CashMovementType, string>> = {
  [CashMovementType.CashInToPortfolio]: 'Add cash to one of portfolio banks',
  [CashMovementType.CashOutOfPortfolio]: 'Remove cash from the portfolio',
  [CashMovementType.BankToBank]: 'Move cash between portfolio banks',
  [CashMovementType.InvestorToFund]: 'Add cash to one of portfolio banks',
  [CashMovementType.FundToInvestor]: 'Remove cash from the portfolio',
};

const isNonEmptyFiniteNumber = (value: unknown): value is number | string =>
  (typeof value === 'number' || typeof value === 'string') &&
  (typeof value !== 'string' || value.trim() !== '') &&
  Number.isFinite(Number(value));

export const IssuerAddCashMovementModal = ({
  open,
  onClose,
  item,
  portfolioId,
}: IssuerAddCashMovementModalProps) => {
  const registerId = useGetRegister()?.id || null;
  const { mutateAsync: createMovement } = useIssuerAddPortfolioCashMovementMutation();
  const { mutateAsync: updateMovement } = useIssuerUpdatePortfolioCashMovementMutation();
  const [fromCurrency, setFromCurrency] = useState<CurrencyEnum | undefined>(
    item?.from_bank?.bank_currency,
  );
  const [toCurrency, setToCurrency] = useState<CurrencyEnum | undefined>(
    item?.to_bank?.bank_currency,
  );
  const [fromAvailableCash, setFromAvailableCash] = useState<number>();
  const [isFromBankBalanceFetching, setIsFromBankBalanceFetching] = useState(false);
  const [isFromFinancialModelingPrep, setIsFromFinancialModelingPrep] = useState(false);

  const formik = useFormik<CreatePortfolioCashMovementWithTotalRequest>({
    initialValues: {
      ...initialValues,
      direction: item?.direction ?? CashMovementType.BankToBank,
      status: item?.status ?? PaymentStatus.Completed,
      amount: item?.amount ?? 0,
      memo: item?.memo ?? '',
      requested_at_date: item?.requested_at_date,
      confirmation_date: item?.confirmation_date,
      from_bank_id: item?.from_bank?.id,
      to_bank_id: item?.to_bank?.id,
      fx_rate: item?.fx_rate,
      total:
        item?.direction === CashMovementType.BankToBank && item.fx_rate
          ? Number(new Big(item.amount).div(item.fx_rate).round(4).valueOf())
          : undefined,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const amountFromBank = Number(
        values.direction === CashMovementType.BankToBank && fromCurrency !== toCurrency
          ? values.total
          : values.amount,
      );
      if (!item && values.from_bank_id && isFromBankBalanceFetching) {
        toast.error('Loading the selected bank balance. Please try again in a moment.');
        return;
      }
      if (
        !item &&
        values.from_bank_id &&
        fromAvailableCash !== undefined &&
        Number.isFinite(amountFromBank) &&
        amountFromBank > fromAvailableCash
      ) {
        toast.error(
          'The selected bank did not have enough available cash on the selected date. Choose a later date or a different bank.',
        );
        return;
      }

      try {
        if (item) {
          await updateMovement({
            id: item.id,
            amount: Number(values.amount),
            direction: values.direction,
            status: values.status,
            memo: values.memo,
            requested_at_date: values.requested_at_date,
            confirmation_date: values.confirmation_date,
            from_bank_id: values.from_bank_id,
            to_bank_id: values.to_bank_id,
            fx_rate: values.direction === CashMovementType.BankToBank ? values.fx_rate : undefined,
          } satisfies UpdatePortfolioCashMovementRequest);
        } else {
          await createMovement({
            ...values,
            fx_rate: values.direction === CashMovementType.BankToBank ? values.fx_rate : undefined,
          });
        }
        resetForm();
        onClose();
      } catch {
        // The API error has already been displayed by withErrorHandling.
      }
    },
    enableReinitialize: true,
  });

  const fxDate = formik.values.requested_at_date
    ? dayjs(formik.values.requested_at_date).format('YYYY-MM-DD')
    : undefined;
  const fxContextKey = [
    formik.values.direction,
    formik.values.from_bank_id,
    formik.values.to_bank_id,
    fxDate,
  ].join(':');
  const originalFxContextKey = item
    ? [
        item.direction,
        item.from_bank?.id,
        item.to_bank?.id,
        item.requested_at_date ? dayjs(item.requested_at_date).format('YYYY-MM-DD') : undefined,
      ].join(':')
    : undefined;
  const fxContextChanged = !!item && fxContextKey !== originalFxContextKey;
  const hasFxContext =
    formik.values.direction === CashMovementType.BankToBank &&
    !!formik.values.from_bank_id &&
    !!formik.values.to_bank_id &&
    !!fxDate &&
    !!fromCurrency &&
    !!toCurrency;
  const shouldFetchHistoricalFx =
    hasFxContext && fromCurrency !== toCurrency && (!item || fxContextChanged);
  const { data: dataCurrencyHistoricalFxRate, isFetching: isFetchingCurrencyHistoricalFxRate } =
    useIssuerCurrencyHistoricalFxRateQuery(
      { from_currency: fromCurrency, to_currency: toCurrency, fx_date: fxDate },
      !shouldFetchHistoricalFx,
    );
  const previousFxContextKey = useRef(fxContextKey);
  const manuallyEditedFxContextKey = useRef<string | null>(null);
  const appliedHistoricalFxKey = useRef<string | null>(null);

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: registerId as number }, { disabled: !registerId });

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registerShareholderList?.shareholders) return [];

    return registerShareholderList?.shareholders.map(item => ({
      label: item.entity.entity_name,
      value: item.id,
    }));
  }, [registerShareholderList]);

  const handleChangeShareholder = (id: number | null) => {
    formik.setFieldValue('shareholder_id', id);
    formik.setFieldError('shareholder_id', '');
  };

  const handleChangeDirection = useCallback((direction: CashMovementType | null) => {
    formik.setFieldValue('direction', direction);
    formik.setFieldError('direction', '');
    if (direction !== CashMovementType.BankToBank) {
      setFromCurrency(undefined);
      setToCurrency(undefined);
      formik.setFieldValue('fx_rate', undefined);
      formik.setFieldError('fx_rate', '');
    }
    if (
      direction === CashMovementType.InvestorToFund ||
      direction === CashMovementType.BankToBank ||
      direction === CashMovementType.CashInToPortfolio
    ) {
      formik.setFieldValue('from_bank_id', undefined);
      formik.setFieldError('from_bank_id', '');
    }
    if (
      direction === CashMovementType.FundToInvestor ||
      direction === CashMovementType.BankToBank ||
      direction === CashMovementType.CashOutOfPortfolio
    ) {
      formik.setFieldValue('to_bank_id', undefined);
      formik.setFieldError('to_bank_id', '');
    }
    if (
      direction === CashMovementType.BankToBank ||
      direction === CashMovementType.CashInToPortfolio ||
      direction === CashMovementType.CashOutOfPortfolio
    ) {
      formik.setFieldValue('shareholder_id', undefined);
      formik.setFieldError('shareholder_id', '');
      handleChangeStatus(PaymentStatus.Completed);
    }
  }, []);

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | null) => {
      formik.setFieldValue('status', status);
      formik.setFieldError('status', '');
      if (status === PaymentStatus.Pending) formik.setFieldValue('confirmation_date', null);
      if (status === PaymentStatus.Completed)
        formik.setFieldValue('confirmation_date', formik.values.requested_at_date);
    },
    [formik.values.requested_at_date],
  );

  const handleChangeDate = (
    field: 'requested_at_date' | 'confirmation_date',
    date: Dayjs | null,
  ) => {
    formik.setFieldValue(field, date ? date.toISOString() : date);
    formik.setFieldError(field, '');
  };

  const handleChangeBank = useCallback(
    (fieldName: keyof CreatePortfolioCashMovementRequest) =>
      (item: SelectPaginateOption<RegisterBankExtended> | null) => {
        formik.setFieldValue(fieldName, item?.value.id);
        formik.setFieldError(fieldName, '');
        if (fieldName === 'to_bank_id') {
          setToCurrency(item?.value.bank_currency);
        }
        if (fieldName === 'from_bank_id') {
          setFromCurrency(item?.value.bank_currency);
        }
      },
    [],
  );

  const handleSelectedFromBankChange = useCallback((bank: RegisterBankExtended | null) => {
    setFromAvailableCash(
      bank?.total_available_cash !== undefined && bank?.total_available_cash !== null
        ? +bank.total_available_cash
        : undefined,
    );
  }, []);

  const directionOptions = useMemo<SelectWithCreatableOption<CashMovementType, ReactNode>[]>(() => {
    let items = getSelectOptionByEnum<CashMovementType>(CashMovementType, false, false).map(
      item => ({
        ...item,
        label: (
          <div className="flex items-center gap-2">
            <div>{mapCashMovementTypeToText[item.value] || item.label}</div>
            <span className="z-[1]">
              <TooltipNew
                text={mapCashMovementTypeToDescription[item.value] || ''}
                positionStrategy={'fixed'}
              >
                <img alt="info" src={InfoCircleIcon} width={18} />
              </TooltipNew>
            </span>
          </div>
        ),
      }),
    );

    items = items.filter(
      item =>
        item.value !== CashMovementType.FundToInvestor &&
        item.value !== CashMovementType.InvestorToFund,
    );

    return items;
  }, []);

  const shouldShowFromBank =
    formik.values.direction === CashMovementType.FundToInvestor ||
    formik.values.direction === CashMovementType.BankToBank ||
    formik.values.direction === CashMovementType.CashOutOfPortfolio;

  const shouldShowToBank =
    formik.values.direction === CashMovementType.InvestorToFund ||
    formik.values.direction === CashMovementType.CashInToPortfolio ||
    formik.values.direction === CashMovementType.BankToBank;

  const shouldShowShareholder =
    formik.values.direction === CashMovementType.FundToInvestor ||
    formik.values.direction === CashMovementType.InvestorToFund;

  const shouldShowRate =
    formik.values.direction === CashMovementType.BankToBank &&
    !!formik.values.from_bank_id &&
    !!formik.values.to_bank_id &&
    !!formik.values.requested_at_date &&
    toCurrency !== fromCurrency;

  const shouldShowStatus =
    formik.values.direction !== CashMovementType.CashInToPortfolio &&
    formik.values.direction !== CashMovementType.BankToBank &&
    formik.values.direction !== CashMovementType.CashOutOfPortfolio;

  const shouldShowConfirmationDate =
    formik.values.status === PaymentStatus.Completed &&
    formik.values.direction !== CashMovementType.CashInToPortfolio &&
    formik.values.direction !== CashMovementType.BankToBank &&
    formik.values.direction !== CashMovementType.CashOutOfPortfolio;

  const withdrawalBalanceDate =
    formik.values.status === PaymentStatus.Completed
      ? formik.values.confirmation_date || formik.values.requested_at_date
      : formik.values.requested_at_date;
  const previousBankWithdrawal =
    item?.direction === CashMovementType.CashOutOfPortfolio &&
    item.status === PaymentStatus.Completed &&
    item.from_bank?.id === formik.values.from_bank_id &&
    item.confirmation_date &&
    withdrawalBalanceDate &&
    !dayjs(withdrawalBalanceDate).isBefore(dayjs(item.confirmation_date))
      ? Number(item.amount)
      : 0;
  const bankWithdrawAllAmount = withdrawAllAmount(
    fromAvailableCash === undefined
      ? undefined
      : Number(new Big(fromAvailableCash).plus(previousBankWithdrawal).toString()),
    2,
  );
  const showBankWithdrawAll =
    formik.values.direction === CashMovementType.CashOutOfPortfolio &&
    !!formik.values.from_bank_id &&
    !isFromBankBalanceFetching &&
    bankWithdrawAllAmount !== undefined;

  const handleUpdateTotal = useCallback((rate: number, amount: number, toBank = false) => {
    const total = !amount
      ? 0
      : !rate
      ? amount
      : toBank
      ? Number(
          new Big(amount)
            .mul(rate || 1)
            .round(4)
            .valueOf(),
        )
      : Number(
          new Big(amount)
            .div(rate || 1)
            .round(4)
            .valueOf(),
        );
    formik.setFieldValue(toBank ? 'amount' : 'total', total);
    formik.setFieldError(toBank ? 'amount' : 'total', '');
  }, []);

  const handleChangeAmount = useCallback(
    (amount: string | number) => {
      const parsedAmount = Number(amount);
      const isValidAmount = isNonEmptyFiniteNumber(amount);

      formik.setFieldValue('amount', isValidAmount ? amount : '');
      formik.setFieldError('amount', '');
      const rate = formik.values.fx_rate;
      const parsedRate = Number(rate);
      if (isValidAmount && isNonEmptyFiniteNumber(rate)) {
        handleUpdateTotal(parsedRate, parsedAmount);
      }
    },
    [formik.values.fx_rate],
  );

  const handleChangeRate = useCallback(
    (rate: string | number) => {
      const parsedRate = Number(rate);
      const isValidRate = isNonEmptyFiniteNumber(rate);

      formik.setFieldValue('fx_rate', isValidRate ? rate : '');
      formik.setFieldError('fx_rate', '');
      const amount = formik.values.amount;
      const parsedAmount = Number(amount);
      if (isValidRate && isNonEmptyFiniteNumber(amount)) {
        handleUpdateTotal(parsedRate, parsedAmount);
      }
    },
    [formik.values.amount],
  );

  useEffect(() => {
    if (previousFxContextKey.current === fxContextKey) return;
    previousFxContextKey.current = fxContextKey;
    manuallyEditedFxContextKey.current = null;
    appliedHistoricalFxKey.current = null;
    setIsFromFinancialModelingPrep(false);
    if (!hasFxContext) {
      formik.setFieldValue('fx_rate', undefined);
    } else if (fromCurrency === toCurrency) {
      handleChangeRate(1);
    } else if (item && !fxContextChanged) {
      handleChangeRate(item.fx_rate ?? '');
    } else {
      formik.setFieldValue('fx_rate', undefined);
    }
  }, [
    formik,
    fromCurrency,
    fxContextChanged,
    fxContextKey,
    handleChangeRate,
    hasFxContext,
    item,
    toCurrency,
  ]);

  useEffect(() => {
    if (!shouldFetchHistoricalFx || isFetchingCurrencyHistoricalFxRate) return;
    if (manuallyEditedFxContextKey.current === fxContextKey) return;
    const price = dataCurrencyHistoricalFxRate?.data?.price;
    const historicalFxKey = `${fxContextKey}:${price ?? ''}`;
    if (appliedHistoricalFxKey.current === historicalFxKey) return;
    appliedHistoricalFxKey.current = historicalFxKey;
    setIsFromFinancialModelingPrep(!!price);
    handleChangeRate(price || '');
  }, [
    dataCurrencyHistoricalFxRate,
    fxContextKey,
    handleChangeRate,
    isFetchingCurrencyHistoricalFxRate,
    shouldFetchHistoricalFx,
  ]);

  const onChangeTotal = useCallback(
    (amount: number | string) => {
      const parsedAmount = Number(amount);
      const isValidAmount = isNonEmptyFiniteNumber(amount);

      formik.setFieldValue('total', isValidAmount ? amount : '');
      formik.setFieldError('total', '');
      const rate = formik.values.fx_rate;
      const parsedRate = Number(rate);
      if (isValidAmount && isNonEmptyFiniteNumber(rate)) {
        handleUpdateTotal(parsedRate, parsedAmount, true);
      }
    },
    [formik.values.fx_rate],
  );

  const getDisabledFutureDate = (date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day');

  return (
    <ModalContentWrapper
      open={open}
      title={item ? 'Edit cash movement' : 'Add cash movement'}
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-6" onSubmit={preventFormDefault}>
          <Select
            label="Direction"
            options={directionOptions}
            value={formik.values.direction}
            onChange={handleChangeDirection}
            error={formik.errors.direction}
            disabled={!!item}
            isClearable={!item}
          />

          {shouldShowFromBank && (
            <RegisterBanksSelect
              onChange={handleChangeBank('from_bank_id')}
              value={formik.values.from_bank_id}
              label="From Bank"
              key={`From_Bank_direction_${formik.values.direction}`}
              clearable={true}
              error={formik.errors.from_bank_id}
              needBankType={
                mapCashMovementDirectionToBankType[formik.values.direction as CashMovementType]
              }
              excludeIds={formik.values.to_bank_id ? [formik.values.to_bank_id] : undefined}
              prefillBank={
                formik.values.direction === CashMovementType.FundToInvestor ||
                formik.values.direction === CashMovementType.CashOutOfPortfolio
              }
              showAvailableCashInsteadOfTypes={true}
              onSelectedBankChange={handleSelectedFromBankChange}
              onOptionsMetaChange={({ isFetching }) => setIsFromBankBalanceFetching(isFetching)}
              date_to={withdrawalBalanceDate}
              balanceAtExactTime={true}
              portfolio_id={portfolioId}
              is_overall={true}
            />
          )}
          {shouldShowToBank && (
            <RegisterBanksSelect
              onChange={handleChangeBank('to_bank_id')}
              value={formik.values.to_bank_id}
              label="To Bank"
              key={`To_Bank_direction_${formik.values.direction}`}
              clearable={true}
              error={formik.errors.to_bank_id}
              needBankType={
                mapCashMovementDirectionToBankType[formik.values.direction as CashMovementType]
              }
              excludeIds={formik.values.from_bank_id ? [formik.values.from_bank_id] : undefined}
              prefillBank={
                formik.values.direction === CashMovementType.InvestorToFund ||
                formik.values.direction === CashMovementType.CashInToPortfolio
              }
              showAvailableCashInsteadOfTypes={true}
              date_to={formik.values.requested_at_date || formik.values.confirmation_date}
              portfolio_id={portfolioId}
              is_overall={true}
            />
          )}
          {shouldShowShareholder && (
            <Select
              label="Shareholder"
              options={registerShareholderListOptions}
              isLoading={isRegisterShareholderListLoading}
              value={formik.values.shareholder_id}
              onChange={handleChangeShareholder}
              error={formik.errors.shareholder_id}
            />
          )}

          <CalendarInput
            label="Date"
            onChange={date => {
              handleChangeDate('requested_at_date', date);
              handleChangeDate('confirmation_date', date);
            }}
            error={formik.errors.requested_at_date || formik.errors.confirmation_date}
            value={formik.values.requested_at_date ? dayjs(formik.values.requested_at_date) : null}
            showTime={{ format: 'HH:mm' }}
            format="DD-MM-YYYY HH:mm"
            disabledDate={getDisabledFutureDate}
            disabledTime={date => getDisableTime(date, true)}
          />

          {shouldShowRate && (
            <TooltipNew
              text={`${goodFormatWithCurrency(toCurrency?.toUpperCase())(
                formik.values.fx_rate || 0,
              )} = ${goodFormatWithCurrency(fromCurrency?.toUpperCase())(1)}`}
            >
              <CurrencyInput
                label={
                  <div className="flex items-center gap-4">
                    <span>
                      Exchange Rate
                      {isFromFinancialModelingPrep && (
                        <>
                          {' '}
                          <span className="font-bold font-italic">
                            provided by Financial Modeling Prep
                          </span>
                        </>
                      )}
                    </span>
                    {isFetchingCurrencyHistoricalFxRate && <Spinner size="xs" className="m-0" />}
                  </div>
                }
                decimalPoints={6}
                disabled={isFetchingCurrencyHistoricalFxRate}
                error={formik.errors.fx_rate}
                value={formik.values.fx_rate}
                onChange={price => {
                  handleChangeRate(price);
                  manuallyEditedFxContextKey.current = fxContextKey;
                  setIsFromFinancialModelingPrep(false);
                }}
                currency={toCurrency?.toUpperCase() || ''}
              />
            </TooltipNew>
          )}

          <CurrencyInput
            label={
              <div className="flex items-center gap-4">
                <span>Amount{shouldShowRate ? ' to bank' : ''}</span>
                {showBankWithdrawAll && (
                  <span>
                    (
                    <button
                      type="button"
                      className="cursor-pointer hover:underline text-blue-500"
                      onClick={() => {
                        if (bankWithdrawAllAmount !== undefined) {
                          handleChangeAmount(bankWithdrawAllAmount);
                        }
                      }}
                    >
                      WITHDRAW ALL
                    </button>
                    )
                  </span>
                )}
              </div>
            }
            error={formik.errors.amount}
            value={formik.values.amount}
            onChange={handleChangeAmount}
            currency={toCurrency?.toUpperCase()}
          />

          {shouldShowRate && (
            <CurrencyInput
              name="total"
              label="Amount from Bank"
              error={formik.errors.total}
              value={formik.values.total ?? ''}
              onChange={onChangeTotal}
              currency={fromCurrency?.toUpperCase() || ''}
              decimalPoints={4}
            />
          )}

          {shouldShowStatus && (
            <Select
              label="Status"
              options={getSelectOptionByEnum<PaymentStatus>(PaymentStatus).filter(
                ({ value }) => value !== PaymentStatus.Unsuccessfull,
              )}
              value={formik.values.status}
              onChange={handleChangeStatus}
              error={formik.errors.status}
            />
          )}

          {shouldShowConfirmationDate && (
            <CalendarInput
              label="Confirmation date"
              onChange={handleChangeDate.bind(null, 'confirmation_date')}
              error={formik.errors.confirmation_date}
              value={
                formik.values.confirmation_date ? dayjs(formik.values.confirmation_date) : null
              }
              showTime={{ format: 'HH:mm' }}
              format="DD-MM-YYYY HH:mm"
              disabledDate={getDisabledFutureDate}
              disabledTime={date => getDisableTime(date, true)}
            />
          )}

          <TextInputField label="Memo (Optional)" name="memo" placeholder="Memo..." />
          <Button onClick={formik.submitForm} loading={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
